import { CalendarClock, CircleCheck, CircleX, Loader2, MailCheck, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BeadDivider } from "./BeadDivider";
import { useCommunityActions } from "@/hooks/useCommunityActions";
import type { ApplicationRecord } from "@/hooks/useChama";
import type { ReceivedInvite } from "@/lib/concord/invites";
import type { ReceivedJoinResponse } from "@/lib/concord/requests";
import type { CommunityListState } from "@/lib/concord/membership";
import type { Membership } from "@/lib/concord/types";

/** "Application received" — shown while the moderator reviews. */
export function PendingCard({
  application,
  onReapply,
}: {
  application: ApplicationRecord;
  onReapply: () => void;
}) {
  return (
    <Card className="shadow-md border-gold/40">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-gold/15">
          <MailCheck className="size-6 text-gold" />
        </div>
        <CardTitle className="font-display text-2xl">Application received</CardTitle>
        <CardDescription>
          Sent{" "}
          {new Date(application.at).toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
          . The moderator reviews every application personally — you'll find
          your invite here (and in any Concord client, like Armada) once
          you're approved.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg bg-muted p-4 text-sm space-y-2">
          <p>
            <span className="font-medium">Who you are: </span>
            <span className="text-muted-foreground line-clamp-2">{application.request.about}</span>
          </p>
          <p>
            <span className="font-medium">Why you want to join: </span>
            <span className="text-muted-foreground line-clamp-2">{application.request.why}</span>
          </p>
        </div>
        <BeadDivider />
        <Button variant="outline" className="w-full" onClick={onReapply}>
          Edit &amp; resubmit application
        </Button>
      </CardContent>
    </Card>
  );
}

/** "You're invited" — a Direct Invite (CORD-05 §6) is waiting. */
export function InvitedCard({
  invite,
  membership,
  communityList,
  onAccepted,
}: {
  invite: ReceivedInvite;
  membership: Membership | undefined;
  communityList: CommunityListState;
  onAccepted: () => void;
}) {
  const { acceptInvite } = useCommunityActions({
    membership,
    communityList,
    onChanged: onAccepted,
  });
  return (
    <Card className="shadow-md border-accent/40">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-accent/15">
          <CircleCheck className="size-6 text-accent" />
        </div>
        <CardTitle className="font-display text-2xl">Karibu! You're invited</CardTitle>
        <CardDescription>
          The moderator approved your application. Accept to receive the
          chama's keys and join{" "}
          <span className="font-medium text-foreground">{invite.bundle.name}</span>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button
          size="lg"
          className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
          disabled={acceptInvite.isPending}
          onClick={() => acceptInvite.mutate(invite.bundle)}
        >
          {acceptInvite.isPending && <Loader2 className="size-4 animate-spin" />}
          Accept invite &amp; join
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          Accepting stores the community keys in your encrypted Community List
          (kind 33302) — the same vault Armada reads, so the chama appears
          there too.
        </p>
      </CardContent>
    </Card>
  );
}

/** The moderator declined — with her note, and a path to reapply. */
export function DeclinedCard({
  decline,
  onReapply,
}: {
  decline: ReceivedJoinResponse;
  onReapply: () => void;
}) {
  return (
    <Card className="shadow-md border-destructive/30">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-destructive/10">
          <CircleX className="size-6 text-destructive" />
        </div>
        <CardTitle className="font-display text-2xl">Not this time</CardTitle>
        <CardDescription>The moderator declined your application.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {decline.response.message && (
          <blockquote className="rounded-lg bg-muted p-4 text-sm text-muted-foreground italic">
            “{decline.response.message}”
          </blockquote>
        )}
        <Button variant="outline" className="w-full" onClick={onReapply}>
          Apply again
        </Button>
      </CardContent>
    </Card>
  );
}

/** The user's signer can't do NIP-44, which Concord requires. */
export function UnsupportedCard() {
  return (
    <Card className="shadow-md border-destructive/30">
      <CardHeader className="text-center">
        <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-destructive/10">
          <ShieldAlert className="size-6 text-destructive" />
        </div>
        <CardTitle className="font-display text-2xl">Signer too old</CardTitle>
        <CardDescription>
          Chama Chat is end-to-end encrypted with NIP-44. Your login method
          doesn't support it — try a NIP-07 extension like Alby, a fresh nsec
          login, or an up-to-date remote signer.
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

/** Small footer note with the review timeline expectation. */
export function ReviewNote() {
  return (
    <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
      <CalendarClock className="size-3.5" />
      Applications are usually reviewed within a few days.
    </p>
  );
}

export function LoadingGate() {
  return (
    <Card className="shadow-md">
      <CardContent className="py-12 px-8 text-center space-y-4">
        <Loader2 className="size-6 animate-spin mx-auto text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Unlocking your community vault…
        </p>
      </CardContent>
    </Card>
  );
}

export function ApplyAgainState({ onReapply }: { onReapply: () => void }) {
  return (
    <Button variant="outline" className="w-full" onClick={onReapply}>
      Apply again
    </Button>
  );
}
